import { useEffect } from 'react';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Form, Input, Modal, Select, Tooltip, message } from 'antd';
import dayjs from 'dayjs';
import useEmployeeNoticesT, { errorText } from '@/components/email-templates/useEmployeeNoticesT';
import { EMPLOYEE_EQUIPMENT_QUERY_KEY } from '@/components/email-templates/constants';
import {
  submitEquipmentRequest,
  submitOwnEquipmentRequest,
} from '@/services/equipmentRequests.service';
import { DATE_FORMATS } from '@/utils/formUtils';
import { repeatedLines } from './equipmentLines';

// A service that can receive: it exists and has an address.
const canReceive = (service) => !!service?.emails?.length;

/** "→ IT", the service's addresses on hover. */
function ServiceTag({ service, tn }) {
  return (
    <Tooltip title={service.emails.join(', ')}>
      <span className="cursor-help text-[11.5px] text-zinc-500 underline decoration-dotted">
        {tn('goesTo', { to: service.name })}
      </span>
    </Tooltip>
  );
}

/** Two round buttons, YES and NO; clicking the chosen one again clears it. */
function YesNoButtons({ value, onChange, disabled, tn }) {
  return (
    <div className="flex gap-1.5">
      {['yes', 'no'].map((option) => (
        <Button
          key={option}
          size="small"
          disabled={disabled}
          type={value === option ? 'primary' : 'default'}
          className="!min-w-[56px] !rounded-[50px]"
          onClick={() => onChange(value === option ? undefined : option)}
        >
          {tn(option === 'yes' ? 'answerYes' : 'answerNo')}
        </Button>
      ))}
    </div>
  );
}

// The items picked in a group, always as a list (one-item groups hold one name).
const pickedOf = (value) => {
  if (Array.isArray(value)) return value;
  return value ? [value] : [];
};

/**
 * One group of the company's list on the form: a drop-down to pick several
 * items (searchable), one item, or YES / NO buttons for a question; under
 * it a detail field for every picked item the list asks details for.
 */
function GroupField({ group, form, tn }) {
  const picked = pickedOf(Form.useWatch(['picks', group.key], form));
  const groupReceives = canReceive(group.service);
  const options = group.items.map((item) => {
    const ownService =
      canReceive(item.service) && item.service.name !== group.service?.name ? item.service : null;
    return {
      value: item.name,
      label: ownService ? `${item.name} (${tn('goesTo', { to: ownService.name })})` : item.name,
      disabled: !canReceive(item.service),
    };
  });
  const withDetail = group.items.filter((item) => item.withDetail && picked.includes(item.name));

  return (
    <div className="grid gap-1 border-t border-zinc-100 pt-2.5 first:border-t-0 first:pt-0">
      <div className="flex flex-wrap items-baseline justify-between gap-x-2">
        <span className="text-[12.5px] font-bold text-zinc-700">
          {group.implicit ? tn('equipmentItems') : group.name}
        </span>
        {groupReceives && <ServiceTag service={group.service} tn={tn} />}
      </div>
      {group.mode === 'yesNo' ? (
        <Form.Item name={['answers', group.key]} noStyle>
          <YesNoButtons disabled={!groupReceives} tn={tn} />
        </Form.Item>
      ) : (
        <Form.Item name={['picks', group.key]} noStyle>
          <Select
            mode={group.mode === 'single' ? undefined : 'multiple'}
            allowClear
            showSearch
            optionFilterProp="label"
            options={options}
            placeholder={tn(group.mode === 'single' ? 'pickOneItem' : 'pickItems')}
            notFoundContent={tn('noItemsFound')}
            className="w-full"
          />
        </Form.Item>
      )}
      {withDetail.map((item) => (
        <div
          key={item.name}
          className="grid grid-cols-[minmax(0,160px)_minmax(0,1fr)] items-center gap-2 pl-2"
        >
          <span className="truncate text-[12px] text-zinc-600">{item.name}</span>
          <Form.Item name={['details', group.key, item.name]} noStyle>
            <Input size="small" placeholder={tn('detailPlaceholder')} maxLength={300} />
          </Form.Item>
        </div>
      ))}
    </div>
  );
}

/**
 * Equipment form for one employee: who it is for (position, sector and
 * whether they are new, worked out by the platform), then the company's
 * groups — drop-downs to pick items from, YES / NO questions — with a
 * detail field (model, code…) for the items that ask for one; items not on
 * the list (to the service set for them) and a note. Each service gets one
 * email with only its items, grouped as here, HR in copy.
 *
 * @param {Array<{key:string, name:string, mode:'multi'|'single'|'yesNo', implicit:boolean,
 *   service:{name:string, emails:string[]}|null, items:Array<{name:string, withDetail:boolean,
 *   service:{name:string, emails:string[]}|null}>}>} props.groups
 * @param {{name:string, emails:string[]}|null} props.otherItemsService - Where typed-in items go.
 * @param {{ newHire:boolean, position:string, sectorName:string }} [props.header]
 * @param {boolean} [props.self] - The employee asking for themselves: the
 *   request first goes to the head of their sector for approval.
 * @param {Array<Object>} [props.previousRequests] - To warn before asking for
 *   something again; sending stays allowed.
 * @param {boolean} [props.fillWaiting] - A request to the head waits: sending
 *   from here closes it.
 */
export default function EquipmentRequestModal({
  open,
  onClose,
  employeeId,
  employeeName,
  groups = [],
  otherItemsService = null,
  header = null,
  self = false,
  previousRequests = [],
  fillWaiting = false,
}) {
  const { t, tn } = useEmployeeNoticesT();
  const [form] = Form.useForm();
  const [modal, modalHolder] = Modal.useModal();
  const queryClient = useQueryClient();
  const picks = Form.useWatch('picks', form) || {};
  const answers = Form.useWatch('answers', form) || {};
  const extrasWatched = Form.useWatch('extras', form) || [];

  const extrasAllowed = canReceive(otherItemsService);
  const canSend =
    groups.some((group) =>
      group.mode === 'yesNo'
        ? canReceive(group.service)
        : group.items.some((item) => canReceive(item.service))
    ) || extrasAllowed;
  const hasExtras = extrasWatched.some((row) => row?.name?.trim());
  // How many emails the choices make: one per service.
  const emailsCount = new Set([
    ...groups.flatMap((group) => {
      if (group.mode === 'yesNo') {
        return answers[group.key] === 'yes' && canReceive(group.service) ? [group.service.name] : [];
      }
      const names = pickedOf(picks[group.key]);
      return group.items
        .filter((item) => names.includes(item.name) && canReceive(item.service))
        .map((item) => item.service.name);
    }),
    ...(hasExtras && extrasAllowed ? [otherItemsService.name] : []),
  ]).size;

  useEffect(() => {
    if (open) form.resetFields();
  }, [open, form]);

  const submit = useMutation({
    mutationFn: (payload) =>
      self
        ? submitOwnEquipmentRequest(employeeId, payload)
        : submitEquipmentRequest(employeeId, payload),
    onSuccess: (result) => {
      if (result?.sendFailed) message.warning(result.message);
      else message.success(result?.message || tn('equipmentSent'));
      queryClient.invalidateQueries({ queryKey: [EMPLOYEE_EQUIPMENT_QUERY_KEY, employeeId] });
      onClose();
    },
    onError: (error) => message.error(errorText(error, tn('sendFailed'))),
  });

  const onFinish = (values) => {
    const chosen = groups
      .filter((group) => group.mode !== 'yesNo')
      .flatMap((group) =>
        pickedOf(values.picks?.[group.key]).map((name) => ({
          name,
          groupKey: group.key,
          detail: values.details?.[group.key]?.[name] || '',
        }))
      );
    const answered = groups
      .filter((group) => group.mode === 'yesNo' && values.answers?.[group.key])
      .map((group) => ({ groupKey: group.key, value: values.answers[group.key] }));
    const extras = (values.extras || [])
      .filter((row) => row?.name?.trim())
      .map((row) => ({ name: row.name.trim(), detail: row.detail || '' }));
    if (!chosen.length && !extras.length && !answered.some((answer) => answer.value === 'yes')) {
      message.warning(tn('nothingPicked'));
      return;
    }
    const payload = { items: chosen, answers: answered, extras, note: values.note || '' };
    const yesAnswers = groups.filter((group) => group.mode === 'yesNo' && values.answers?.[group.key] === 'yes');
    const repeated = repeatedLines(
      [
        ...chosen.map((line) => line.name),
        ...extras.map((line) => line.name),
        ...yesAnswers.map((group) => group.name),
      ],
      previousRequests
    );
    if (!repeated.length) {
      submit.mutate(payload);
      return;
    }
    modal.confirm({
      title: tn('equipmentRepeat.title'),
      content: (
        <div className="grid gap-1.5 text-[13px]">
          <span>{tn('equipmentRepeat.text')}</span>
          <ul className="m-0 pl-4">
            {repeated.map((line) => (
              <li key={line.name}>
                {tn('equipmentRepeat.lineDate', {
                  name: line.name,
                  date: dayjs(line.date).format(DATE_FORMATS.DISPLAY_DOTS),
                })}
              </li>
            ))}
          </ul>
          <span className="font-semibold">{tn('equipmentRepeat.question')}</span>
        </div>
      ),
      okText: tn('equipmentRepeat.send'),
      cancelText: tn('equipmentRepeat.back'),
      okButtonProps: { className: '!rounded-[50px]' },
      cancelButtonProps: { className: '!rounded-[50px]' },
      onOk: () => submit.mutate(payload),
    });
  };

  const facts = header
    ? [
        header.position && [tn('equipmentPosition'), header.position],
        header.sectorName && [tn('equipmentSector'), header.sectorName],
        [tn('equipmentNewHire'), tn(header.newHire ? 'answerYes' : 'answerNo')],
      ].filter(Boolean)
    : [];

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={680}
      destroyOnHidden
      title={
        self
          ? tn('ownEquipmentTitle')
          : tn('equipmentTitle', { name: employeeName })
      }
      okText={tn('sendRequest')}
      cancelText={t('cancel', { ns: 'forms' })}
      okButtonProps={{
        loading: submit.isPending,
        disabled: !canSend,
        className: '!rounded-[50px]',
      }}
      cancelButtonProps={{ className: '!rounded-[50px]' }}
      onOk={() => form.submit()}
    >
      {modalHolder}
      {self && canSend && (
        <Alert
          type="info"
          showIcon
          className="!mb-3"
          message={tn('ownEquipmentApprovalInfo')}
        />
      )}
      {fillWaiting && (
        <Alert type="warning" showIcon className="!mb-3" message={tn('equipmentFill.waitingNotice')} />
      )}
      {!canSend && (
        <Alert type="warning" showIcon className="!mb-3" message={tn('noEquipmentRecipients')} />
      )}
      {facts.length > 0 && (
        <div className="mb-3 flex flex-wrap gap-x-4 gap-y-1 rounded-lg bg-zinc-50 px-3 py-2 text-[12.5px]">
          {facts.map(([label, value]) => (
            <span key={label}>
              <span className="text-zinc-500">{label}:</span>{' '}
              <span className="font-semibold text-zinc-800">{value}</span>
            </span>
          ))}
        </div>
      )}
      <Form form={form} layout="vertical" onFinish={onFinish} autoComplete="off">
        <div className="grid gap-2.5">
          {groups.map((group) => (
            <GroupField key={group.key} group={group} form={form} tn={tn} />
          ))}
        </div>

        <div className="mb-0.5 mt-4 text-[12.5px] font-bold text-zinc-700">{tn('extraItems')}</div>
        <p className="m-0 mb-1.5 text-[11.5px] text-zinc-500">
          {extrasAllowed ? (
            <ServiceTag service={otherItemsService} tn={tn} />
          ) : (
            tn('extrasNoRecipients')
          )}
        </p>
        <Form.List name="extras">
          {(fields, { add, remove }) => (
            <div className="grid gap-1.5">
              {fields.map((field) => (
                <div
                  key={field.key}
                  className="grid grid-cols-[180px_minmax(0,1fr)_32px] items-center gap-2"
                >
                  <Form.Item name={[field.name, 'name']} noStyle>
                    <Input size="small" placeholder={tn('extraNamePlaceholder')} maxLength={100} />
                  </Form.Item>
                  <Form.Item name={[field.name, 'detail']} noStyle>
                    <Input size="small" placeholder={tn('detailPlaceholder')} maxLength={300} />
                  </Form.Item>
                  <Button
                    size="small"
                    type="text"
                    icon={<DeleteOutlined />}
                    onClick={() => remove(field.name)}
                  />
                </div>
              ))}
              <div>
                <Button
                  size="small"
                  className="!rounded-[50px]"
                  icon={<PlusOutlined />}
                  disabled={!extrasAllowed}
                  onClick={() => add({ name: '', detail: '' })}
                >
                  {tn('addExtra')}
                </Button>
              </div>
            </div>
          )}
        </Form.List>

        <Form.Item name="note" label={tn('note')} className="!mb-0 !mt-4">
          <Input.TextArea rows={3} maxLength={2000} />
        </Form.Item>
        {!self && emailsCount > 0 && (
          <p className="m-0 mt-2 text-[12px] text-zinc-500">
            {tn('equipmentEmailsCount', { count: emailsCount })}
          </p>
        )}
      </Form>
    </Modal>
  );
}
